/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { isNumber } from "utils/APIUtils";
import { isTwoColumnViewMode, isCoverPageMode, getTotalPages, getViewRotationAngle } from "selectors/documentSelectors";
import { getTransformMatrixFromPageGeometry } from "dc-preview-utils/src/Utils/PageUtils";
import { loadStatus, pageOrientation } from "constants/enums";

export const getPage = (stores, pageIndex) => stores.documentStore.pages[pageIndex] || {};

export const getPageBitmap = (stores, pageIndex) =>
  getPage(stores, pageIndex) ? getPage(stores, pageIndex).pageBitmap : null;

export const getPageMountStatus = (stores, pageIndex) => getPage(stores, pageIndex).isMounted;

export const isValidPageIndex = (stores, pageIndex) =>
  isNumber(pageIndex) && pageIndex >= 0 && pageIndex < stores.documentStore.pages.length;

export const getPageTransformMatrix = (stores, pageIndex, options = {}) => {
  if (isValidPageIndex(stores, pageIndex)) {
    const { viewRotation } = options;
    let { shouldUseMediaBox } = options;
    if (shouldUseMediaBox === undefined) shouldUseMediaBox = stores.documentStore.isCropMode;

    const viewRotationAngle = viewRotation === undefined ? getViewRotationAngle(stores) : viewRotation;
    const rotation = (viewRotationAngle + getPageRotation(stores, pageIndex)) % 360;
    const boxes = getPageBoxes(stores, pageIndex);
    return getTransformMatrixFromPageGeometry({ rotation, boxes, shouldUseMediaBox });
  }
};

const getViewRotationMatrixImpl = (angleInDegrees, originX, originY) => {
  const angle = (angleInDegrees * Math.PI) / 180.0;
  const matrix = {
    a: Math.cos(angle),
    b: Math.sin(angle),
    c: -Math.sin(angle),
    d: Math.cos(angle),
    e: -Math.cos(angle) * originX + Math.sin(angle) * originY + originX,
    f: -Math.sin(angle) * originX - Math.cos(angle) * originY + originY
  };
  const pageCorners = [
    { x: 0, y: 0 },
    { x: 0, y: 2 * originY },
    { x: 2 * originX, y: 2 * originY },
    { x: 2 * originX, y: 0 }
  ];
  const newOrigin = pageCorners[(angleInDegrees / 90) % 4];
  const rotatedOrigin = {
    x: matrix.a * newOrigin.x + matrix.c * newOrigin.y + matrix.e,
    y: matrix.b * newOrigin.x + matrix.d * newOrigin.y + matrix.f
  };

  return {
    ...matrix,
    e: matrix.e - rotatedOrigin.x,
    f: matrix.f - rotatedOrigin.y
  };
};

export const getViewRotationMatrix = (stores, pageIndex, rotationAngle) => {
  if (isValidPageIndex(stores, pageIndex)) {
    rotationAngle = rotationAngle !== undefined ? rotationAngle : getViewRotationAngle(stores);
    const isRotated = rotationAngle === 90 || rotationAngle === 270;
    const pageHightBeforeRotation = isRotated ? getPageWidth(stores, pageIndex) : getPageHeight(stores, pageIndex);
    const pageWidthBeforeRotation = isRotated ? getPageHeight(stores, pageIndex) : getPageWidth(stores, pageIndex);
    const originX = pageWidthBeforeRotation / 2;
    const originY = pageHightBeforeRotation / 2;
    return getViewRotationMatrixImpl(rotationAngle, originX, originY);
  }
};

export const getPageOrigHeight = (stores, pageIndex) => {
  let pageHeight = 0;
  if (isValidPageIndex(stores, pageIndex)) {
    pageHeight = stores.documentStore.pages[pageIndex].origHeight
      ? stores.documentStore.pages[pageIndex].origHeight
      : stores.documentStore.pages[0].origHeight;
  }
  return pageHeight;
};
export const getPageOrigWidth = (stores, pageIndex) => {
  let pageWidth = 0;
  if (isValidPageIndex(stores, pageIndex)) {
    pageWidth = stores.documentStore.pages[pageIndex].origWidth
      ? stores.documentStore.pages[pageIndex].origWidth
      : stores.documentStore.pages[0].origWidth;
  }
  return pageWidth;
};

const isRotated = stores => getViewRotationAngle(stores) === 90 || getViewRotationAngle(stores) === 270;

export const getPageViewOrigHeight = (stores, pageIndex) =>
  !isRotated(stores) ? getPageOrigHeight(stores, pageIndex) : getPageOrigWidth(stores, pageIndex);

export const getPageViewOrigWidth = (stores, pageIndex) =>
  !isRotated(stores) ? getPageOrigWidth(stores, pageIndex) : getPageOrigHeight(stores, pageIndex);

export const getPageBitmapSize = (stores, pageIndex) => getPage(stores, pageIndex).bitmapSize;

export const getPageHeight = (stores, pageIndex) => {
  let pageHeight = 0;
  if (isValidPageIndex(stores, pageIndex)) {
    const page = getPage(stores, pageIndex);
    pageHeight = !isRotated(stores) ? page.currPageHeight : page.currPageWidth;
  }
  return pageHeight;
};

export const getPageWidth = (stores, pageIndex) => {
  let pageWidth = 0;
  if (isValidPageIndex(stores, pageIndex)) {
    const page = getPage(stores, pageIndex);
    pageWidth = !isRotated(stores) ? page.currPageWidth : page.currPageHeight;
  }
  return pageWidth;
};

export const isLeftPageIndex = (stores, pageIndex) => {
  let isLeftPage = pageIndex % 2 === 0;
  isLeftPage = isCoverPageMode(stores) ? !isLeftPage : isLeftPage;
  return isLeftPage;
};

export const getPageIndex = (stores, rowIndex, columnIndex) => {
  const pageIndex = isTwoColumnViewMode(stores)
    ? isCoverPageMode(stores)
      ? rowIndex * 2 + columnIndex - 1
      : rowIndex * 2 + columnIndex
    : rowIndex;
  return pageIndex;
};

export const getRowIndex = (stores, pageIndex) => {
  let retVal = -1;
  retVal = isTwoColumnViewMode(stores)
    ? isCoverPageMode(stores)
      ? Math.ceil(pageIndex / 2)
      : Math.floor(pageIndex / 2)
    : pageIndex;
  return retVal;
};

export const getPagesInRow = (stores, rowIndex) => {
  let leftPageIndex = -1;
  let rightPageIndex = -1;
  if (!isTwoColumnViewMode(stores)) {
    leftPageIndex = rowIndex;
  } else {
    leftPageIndex = isCoverPageMode(stores) ? 2 * rowIndex - 1 : 2 * rowIndex;
    rightPageIndex = isCoverPageMode(stores) ? 2 * rowIndex : 2 * rowIndex + 1;
  }
  return { leftPageIndex, rightPageIndex };
};

export const getRowHeight = (stores, rowIndex) => {
  let retVal = 0;
  if (rowIndex >= 0) {
    if (!isTwoColumnViewMode(stores)) {
      return getPageHeight(stores, rowIndex);
    }
    const pagesInRow = getPagesInRow(stores, rowIndex);
    retVal = Math.max(
      getPageHeight(stores, pagesInRow.leftPageIndex),
      getPageHeight(stores, pagesInRow.rightPageIndex)
    );
  }
  return retVal;
};

export const getRowOrigHeight = (stores, rowIndex) => {
  let pageHeight = 0;
  const pagesInRow = getPagesInRow(stores, rowIndex);
  const leftPageHeight = getPageViewOrigHeight(stores, pagesInRow.leftPageIndex);
  const rightPageHeight = getPageViewOrigHeight(stores, pagesInRow.rightPageIndex);
  pageHeight = Math.max(leftPageHeight, rightPageHeight);
  return pageHeight;
};

export const getRowOrigWidth = (stores, rowIndex) => {
  let pageWidth = 0;
  const pagesInRow = getPagesInRow(stores, rowIndex);
  const leftPageWidth = getPageViewOrigWidth(stores, pagesInRow.leftPageIndex);
  const rightPageWidth = getPageViewOrigWidth(stores, pagesInRow.rightPageIndex);
  pageWidth = Math.max(leftPageWidth, rightPageWidth);
  if (isTwoColumnViewMode(stores)) {
    pageWidth *= 2; // Multiplying by 2 so to fit both pages in two column mode
  }
  return pageWidth;
};

export const getTotalRows = stores => {
  const totalPages = getTotalPages(stores);
  const totalRows = isTwoColumnViewMode(stores)
    ? isCoverPageMode(stores)
      ? Math.floor(totalPages / 2) + 1
      : Math.ceil(totalPages / 2)
    : totalPages;
  return totalRows;
};

export const getPageLoadStatus = (stores, pageIndex) => getPage(stores, pageIndex).pageLoadStatus;
export const getPageUsageStatus = (stores, pageIndex) => getPage(stores, pageIndex).pageUsageStatus;
export const shouldTriggerEnterEvent = (stores, pageIndex) => getPage(stores, pageIndex).shouldTriggerEnterEvent;
export const getPageHandle = (stores, pageIndex) => getPage(stores, pageIndex).elementHandle;

export const getPageAccText = (stores, pageIndex) => getPage(stores, pageIndex).pageAccText;

export const getPageParaAccText = (stores, pageIndex) => getPage(stores, pageIndex).paraAccText;

export const getPageTiles = (stores, pageIndex) => {
  const page = getPage(stores, pageIndex);
  return page.useTiles ? page.pageTiles : undefined;
};

export const getTile = (stores, pageIndex, tileIndex) => {
  const pageTiles = getPageTiles(stores, pageIndex);
  return pageTiles ? pageTiles[tileIndex] : undefined;
};

export const getTileBitmap = (stores, pageIndex, tileIndex) => {
  const tile = getTile(stores, pageIndex, tileIndex);
  return tile ? tile.tileBitmap : "";
};

export const getTileLoadStatus = (stores, pageIndex, tileIndex) => {
  const tile = getTile(stores, pageIndex, tileIndex);
  return tile ? tile.tileLoadStatus : 0;
};

export const getTileHeight = (stores, pageIndex, tileIndex) => {
  const tileCoords = getPage(stores, pageIndex).tileCoords[tileIndex];
  return tileCoords.endY - tileCoords.startY;
};

export const getTileWidth = (stores, pageIndex, tileIndex) => {
  const tileCoords = getPage(stores, pageIndex).tileCoords[tileIndex];
  return tileCoords.endX - tileCoords.startX;
};

export const getTileTopCoordinate = (stores, pageIndex, tileIndex) => {
  const tileCoords = getPage(stores, pageIndex).tileCoords[tileIndex];
  return tileCoords.startY;
};

export const getTileLeftCoordinate = (stores, pageIndex, tileIndex) => {
  const tileCoords = getPage(stores, pageIndex).tileCoords[tileIndex];
  return tileCoords.startX;
};

export const getAccessiblePageContentHandle = (stores, pageIndex) =>
  getPage(stores, pageIndex).accessiblePageContentHandle;

export const getPageRotation = (stores, pageIndex) => {
  let tMatrix = stores.documentStore.pages[pageIndex].tMatrix;
  if (tMatrix.a === 0) {
    if (tMatrix.b === -1) {
      return 270;
    }

    return 90;
  } else if (tMatrix.a === -1) {
    return 180;
  }
  return 0;
};

export const getOrigPageRotation = (stores, pageIndex) => stores.documentStore.pages[pageIndex].rotation;

export const isPageBlank = (stores, index) => {
  if (getPageBitmap(stores, index)) {
    return false;
  }
  for (let tileIndex = 0; tileIndex < 4; tileIndex++) {
    if (getTileBitmap(stores, index, tileIndex)) {
      return false;
    }
  }
  return true;
};

export const getAcroformAppearance = (stores, pageIndex, id, data) => {
  const appearanceObj = getPage(stores, pageIndex).getAppearanceDataForField(id);
  return appearanceObj.getAppearance(pageIndex, data);
};

export const unloadAppearances = (stores, pageIndex, id) => {
  const appearanceObj = getPage(stores, pageIndex).getAppearanceDataForField(id);
  return appearanceObj.unloadAppearances();
};

export const getPageHyperlinkLoadStatus = (stores, pageIdx) => getPage(stores, pageIdx).hyperlinkLoadStatus;

export const getPageHyperlinkLoadPriority = (stores, pageIdx) => getPage(stores, pageIdx).hyperlinkLoadPriority;

export const getPageHyperlinkRects = (stores, pageIdx) => {
  if (isValidPageIndex(stores, pageIdx)) {
    return getPage(stores, pageIdx).hyperlinkRects;
  }
  return [];
};

export const getPageHyperlinkPromise = (stores, pageIdx) => getPage(stores, pageIdx).hyperlinkPromise;

export const getPageBoxes = (stores, pageIndex) => {
  let boxes = {};
  if (isValidPageIndex(stores, pageIndex)) {
    const page = getPage(stores, pageIndex);
    boxes = page.boxes;
  }
  return boxes;
};

export const getPageTextLoadPriority = (stores, pageIdx) => getPage(stores, pageIdx).pageTextLoadPriority;
export const getPageTextLoadStatus = (stores, pageIdx) => getPage(stores, pageIdx).pageTextLoadStatus;
export const getPageActions = (stores, pageIndex) => getPage(stores, pageIndex).actions || {};
export const isPageTextLoadFailed = (stores, pageIdx) =>
  getPage(stores, pageIdx).pageTextLoadStatus === loadStatus.FAILED;

export const getPageOrientation = (stores, index) => {
  const height = getPageHeight(stores, index);
  const width = getPageWidth(stores, index);
  let orientation = pageOrientation.NONE;
  if (height > width) {
    orientation = pageOrientation.PORTRAIT;
  } else if (width > height) {
    orientation = pageOrientation.LANDSCAPE;
  }
  return orientation;
};
